import { useAppSelector } from 'modules/state-mgmt/store';
import { useMemo } from 'react';
import { WorkerProfileField } from 'types/profileCustomFields';
import { shouldShowSkippableIndicator } from 'utils/skippableFieldsUtils';
import { extractNationalNumber } from 'utils';

// Generic field configuration interface
interface IFieldConfig {
  isVisible: boolean;
  isMandatory: boolean;
  showSkippableIndicator?: boolean;
  isSkippable?: boolean;
  skippableTill?: number;
}

// Section configuration interfaces
export interface IContactInfoConfig {
  email: IFieldConfig;
  phone: IFieldConfig;
  alternatePhone: IFieldConfig;
  isEitherRequired: boolean;
  isVisible: boolean;
}

export interface IHomeAddressConfig {
  addressLine1: IFieldConfig;
  addressLine2: IFieldConfig;
  country: IFieldConfig;
  state: IFieldConfig;
  city: IFieldConfig;
  zipCode: IFieldConfig;
  isVisible: boolean;
}

export interface IEmergencyContactConfig {
  emergencyContactName: IFieldConfig;
  emergencyContactRelationship: IFieldConfig;
  emergencyContactNumber: IFieldConfig;
  isVisible: boolean;
}

export interface IContactSectionsConfig {
  contactInfo: IContactInfoConfig;
  homeAddress: IHomeAddressConfig;
  emergencyContact: IEmergencyContactConfig;
}

// Field configuration factory with defaults
const createFieldConfig = (
  constraints?: { isVisible?: boolean; isMandatory?: boolean; isSkippable?: boolean; skippableTill?: number },
  defaultVisible: boolean = true,
  defaultMandatory: boolean = false,
): IFieldConfig => ({
  isVisible: constraints?.isVisible ?? defaultVisible,
  isMandatory: constraints?.isMandatory ?? defaultMandatory,
  isSkippable: constraints?.isSkippable,
  skippableTill: constraints?.skippableTill,
});

// Helper to create address section config
const createHomeAddressConfig = (
  profileFieldConstraintsMap: any,
  hasReachedConsentForm?: boolean,
  formValues?: any,
): IHomeAddressConfig => {
  const addressLine1 = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.homeAddressLine1], true, true);
  const addressLine2 = createFieldConfig(
    profileFieldConstraintsMap?.[WorkerProfileField.homeAddressLine2],
    true,
    false,
  );
  const country = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.homeAddressCountry], true, true);
  const state = createFieldConfig(
    profileFieldConstraintsMap?.[WorkerProfileField.homeAddressStateProvince],
    true,
    true,
  );
  const city = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.homeAddressCity], true, true);
  const zipCode = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.homeAddressZipPostal], true, true);

  // Add showSkippableIndicator to each field
  const addSkippableLogic = (field: WorkerProfileField, config: IFieldConfig, formFieldValue: any): IFieldConfig => ({
    ...config,
    showSkippableIndicator:
      hasReachedConsentForm === false
        ? shouldShowSkippableIndicator(field, profileFieldConstraintsMap, hasReachedConsentForm, formFieldValue)
        : false,
  });

  return {
    addressLine1: addSkippableLogic(WorkerProfileField.homeAddressLine1, addressLine1, formValues?.addressLine1),
    addressLine2: addSkippableLogic(WorkerProfileField.homeAddressLine2, addressLine2, formValues?.addressLine2),
    country: addSkippableLogic(WorkerProfileField.homeAddressCountry, country, formValues?.country),
    state: addSkippableLogic(WorkerProfileField.homeAddressStateProvince, state, formValues?.state),
    city: addSkippableLogic(WorkerProfileField.homeAddressCity, city, formValues?.city),
    zipCode: addSkippableLogic(WorkerProfileField.homeAddressZipPostal, zipCode, formValues?.zipCode),
    isVisible: [addressLine1, addressLine2, country, state, city, zipCode].some(config => config.isVisible),
  };
};

// Helper to create emergency contact section config
const createEmergencyContactConfig = (
  profileFieldConstraintsMap: any,
  hasReachedConsentForm?: boolean,
  formValues?: any,
): IEmergencyContactConfig => {
  const emergencyContactName = createFieldConfig(
    profileFieldConstraintsMap?.[WorkerProfileField.emergencyContactName],
    true,
    true,
  );
  const emergencyContactRelationship = createFieldConfig(
    profileFieldConstraintsMap?.[WorkerProfileField.emergencyContactRelationship],
    true,
    true,
  );
  const emergencyContactNumber = createFieldConfig(
    profileFieldConstraintsMap?.[WorkerProfileField.emergencyContactMobilePhone],
    true,
    true,
  );

  // Add showSkippableIndicator to each field
  const addSkippableLogic = (
    field: WorkerProfileField,
    config: IFieldConfig,
    formFieldValue: any,
    isPhoneValue: boolean = false,
  ): IFieldConfig => ({
    ...config,
    showSkippableIndicator:
      hasReachedConsentForm === false
        ? shouldShowSkippableIndicator(
            field,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
            formFieldValue,
            isPhoneValue,
          )
        : false,
  });

  return {
    emergencyContactName: addSkippableLogic(
      WorkerProfileField.emergencyContactName,
      emergencyContactName,
      formValues?.emergencyContactName,
    ),
    emergencyContactRelationship: addSkippableLogic(
      WorkerProfileField.emergencyContactRelationship,
      emergencyContactRelationship,
      formValues?.emergencyContactRelationship,
    ),
    emergencyContactNumber: addSkippableLogic(
      WorkerProfileField.emergencyContactMobilePhone,
      emergencyContactNumber,
      extractNationalNumber(formValues?.emergencyContactNumber),
      true, // isPhoneValue
    ),
    isVisible: [emergencyContactName, emergencyContactRelationship, emergencyContactNumber].some(
      config => config.isVisible,
    ),
  };
};

export const useContactSectionsConfig = (hasReachedConsentForm?: boolean, formValues?: any): IContactSectionsConfig => {
  const { profileFieldConstraintsMap } = useAppSelector(state => ({
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
  }));

  return useMemo(() => {
    // Contact Information Section (with special either/or logic)
    const emailConfig = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.email]);
    const phoneConfig = createFieldConfig(profileFieldConstraintsMap?.[WorkerProfileField.mobilePhoneNumber]);
    const emailOrPhoneConstraints = profileFieldConstraintsMap?.[WorkerProfileField.emailOrMobilePhoneNumber];
    const alternatePhoneConfig = createFieldConfig(
      profileFieldConstraintsMap?.[WorkerProfileField.alternateContactMobilePhoneNumber],
      false, // Default not visible
      false, // Default not mandatory
    );

    const isEitherRequired = emailOrPhoneConstraints?.isMandatory ?? false;
    const shouldShowBoth = emailOrPhoneConstraints?.isVisible ?? false;

    const contactInfoConfig: IContactInfoConfig = {
      email: {
        isVisible: emailConfig.isVisible || shouldShowBoth,
        isMandatory: emailConfig.isMandatory,
        showSkippableIndicator: false, // Primary email cannot be skippable
      },
      phone: {
        isVisible: phoneConfig.isVisible || shouldShowBoth,
        isMandatory: phoneConfig.isMandatory,
        showSkippableIndicator: false, // Primary phone cannot be skippable
      },
      alternatePhone: {
        isVisible: alternatePhoneConfig.isVisible,
        isMandatory: alternatePhoneConfig.isMandatory,
        isSkippable: alternatePhoneConfig.isSkippable,
        skippableTill: alternatePhoneConfig.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.alternateContactMobilePhoneNumber,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                extractNationalNumber(formValues?.phoneNumber),
                true, // isPhoneValue
              )
            : false,
      },
      isEitherRequired,
      isVisible: emailConfig.isVisible || phoneConfig.isVisible || shouldShowBoth || alternatePhoneConfig.isVisible,
    };

    const homeAddressConfig = createHomeAddressConfig(profileFieldConstraintsMap, hasReachedConsentForm, formValues);
    const emergencyContactConfig = createEmergencyContactConfig(
      profileFieldConstraintsMap,
      hasReachedConsentForm,
      formValues,
    );

    return {
      contactInfo: contactInfoConfig,
      homeAddress: homeAddressConfig,
      emergencyContact: emergencyContactConfig,
    };
  }, [profileFieldConstraintsMap, hasReachedConsentForm, formValues]);
};
